import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PageHeader, QuickFilters, DataTable, Tag, Dialog, Button, Field, TextInput, TextArea, Select, Checkbox, ComboBox, Banner, Sheet, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import { OCCUPATION_OPTIONS } from '../../utils/memberStudy.js';
import { AudioPlayer, FormGrid, Row, apiError } from '../../next/ui';
import MemberCardNext from '../members/MemberCardNext.jsx';

/**
 * בקשות רישום ממתינות — new design. Same endpoints as PendingRegistrationsList: the list, approve (with the
 * PHONE_EXISTS → "update existing member" flow and the optional voice notification) and reject.
 */
const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const SYSTEM_ID = String(process.env.REACT_APP_SYSTEM_ID || '').replace(/^["']|["']$/g, '').trim();
const IS_MISHTAMERET = SYSTEM_ID === 'waiting_client';

const STATUS = {
  pending: { label: 'ממתין', tone: 'warning' },
  approved: { label: 'אושר', tone: 'success' },
  rejected: { label: 'נדחה', tone: 'danger' },
};
const EMPTY_FORM = {
  firstName: '', lastName: '', fatherName: '', fatherInLawName: '', idNumber: '', occupation: '',
  city: '', street: '', buildingNumber: '', apartment: '', entrance: '',
  kolelMorning: '', kolelMorningRef: null, kolelAfternoon: '', kolelAfternoonRef: null, kolelEvening: '', kolelEveningRef: null,
  notes: '',
};
const RECORDINGS = [
  ['firstNameRecording', 'firstName', 'שם פרטי'],
  ['lastNameRecording', 'lastName', 'שם משפחה'],
  ['fatherNameRecording', 'fatherName', 'שם אב'],
  ['fatherInLawNameRecording', 'fatherInLawName', 'שם שווער'],
  ['addressRecording', 'address', 'כתובת'],
];
/** the approve dialog also plays the city; in the table it shares the existing city column */
const DIALOG_RECORDINGS = [...RECORDINGS, ['cityRecording', 'city', 'עיר']];
const kolelLabel = (o) => (o ? `${o.name || ''}${o.city ? ` (${o.city})` : ''}${o.code != null ? ` — ${o.code}` : ''}` : '');
const fullName = (m) => [m?.firstName, m?.lastName].filter(Boolean).join(' ') || '—';
const refId = (raw) => (!raw ? null : typeof raw === 'object' ? (raw._id || null) : raw);
const chosenKolels = (r) => (IS_OPEN_GEMARA
  ? (r.kolelMorningName || r.kolelName || '')
  : ([r.kolelMorningName && `לפנה״צ: ${r.kolelMorningName}`, r.kolelAfternoonName && `אחה״צ: ${r.kolelAfternoonName}`, r.kolelEveningName && `ערב: ${r.kolelEveningName}`].filter(Boolean).join(' | ') || r.kolelName || ''));

function classifySchedule(schedule) {
  const s = (schedule || '').trim();
  if (s.includes('בוקר') || s.includes('לפנה') || s.includes('לפני')) return 'morning';
  if (s.includes('אחה') || s.includes('צהר')) return 'afternoon';
  if (s.includes('ערב')) return 'evening';
  return 'unknown';
}

/** /pending-registrations/:id/audio/:field — no ".wav" path in the URL (the Etrog filter blocks recording-proxy) */
const recSrc = (row, rec) => `/pending-registrations/${row._id}/audio/${rec}`;

/** a recorded (IVR) field or the typed value (web) */
function RecOrText({ row, rec, text, label }) {
  if (row.source === 'web' && row[text]) return row[text];
  return row[rec] ? <AudioPlayer src={recSrc(row, rec)} label={label} compact /> : <span className="ck-muted">—</span>;
}

export default function PendingRegistrationsListNext() {
  const toast = useCkToast();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quick, setQuick] = useState('pending');
  const [kolels, setKolels] = useState([]);
  const [notifyEnabled, setNotifyEnabled] = useState(false);

  const [approveTarget, setApproveTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [existingMember, setExistingMember] = useState(null);
  const [sendNotification, setSendNotification] = useState(false);

  const [memberCardId, setMemberCardId] = useState(null);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/pending-registrations', { params: { page: 1, pageSize: 100000 } });
      setAllData(res.data.data || []);
    } catch (e) {
      setError(apiError(e, 'שגיאה בטעינת הנתונים'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (IS_MISHTAMERET) return;
    api.get('/kolels', { params: { pageSize: 500, status: 'active' } }).then((res) => setKolels(res.data.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get('/pending-registrations/notification-settings')
      .then((res) => setNotifyEnabled(Boolean(res.data?.enabled)))
      .catch(() => setNotifyEnabled(false));
  }, []);

  const kolelsForSlot = (slot, currentRef) => {
    const list = kolels.filter((k) => { const c = classifySchedule(k.schedule); return c === slot || c === 'unknown' || (k.schedule || '').includes('כל היום'); });
    if (currentRef && !list.some((k) => k._id === currentRef)) {
      const current = kolels.find((k) => k._id === currentRef);
      if (current) return [current, ...list];
    }
    return list;
  };

  const openApprove = (row) => {
    const isWeb = row.source === 'web';
    setApproveTarget(row);
    setForm({
      ...EMPTY_FORM,
      firstName: isWeb ? (row.firstName || '') : '',
      lastName: isWeb ? (row.lastName || '') : '',
      fatherName: isWeb ? (row.fatherName || '') : '',
      fatherInLawName: isWeb ? (row.fatherInLawName || '') : '',
      idNumber: row.idNumber || '',
      occupation: row.occupation || '',
      city: row.city || '',
      street: isWeb ? (row.address || '') : '',
      kolelMorning: row.kolelMorningName || (!row.kolelAfternoonName && !row.kolelEveningName ? (row.kolelName || '') : ''),
      kolelMorningRef: refId(row.kolelMorningRef) || (!row.kolelAfternoonRef && !row.kolelEveningRef ? refId(row.kolelRef) : null),
      kolelAfternoon: row.kolelAfternoonName || '',
      kolelAfternoonRef: refId(row.kolelAfternoonRef),
      kolelEvening: row.kolelEveningName || '',
      kolelEveningRef: refId(row.kolelEveningRef),
    });
    setApproveError('');
    setExistingMember(null);
    setSendNotification(notifyEnabled);
  };

  const buildPayload = (updateExisting) => {
    let base = form;
    if (IS_MISHTAMERET) base = { ...form, kolelMorning: '', kolelMorningRef: null, kolelAfternoon: '', kolelAfternoonRef: null, kolelEvening: '', kolelEveningRef: null };
    else if (IS_OPEN_GEMARA) base = { ...form, kolelAfternoon: '', kolelAfternoonRef: null, kolelEvening: '', kolelEveningRef: null };
    const withNotify = { ...base, sendNotification: Boolean(sendNotification) };
    return updateExisting ? { ...withNotify, updateExisting: true } : withNotify;
  };

  const approve = async (updateExisting = false) => {
    if (!form.firstName.trim() || !form.lastName.trim()) { setApproveError('יש למלא שם פרטי ושם משפחה'); return; }
    setApproveLoading(true);
    setApproveError('');
    if (!updateExisting) setExistingMember(null);
    try {
      await api.post(`/pending-registrations/${approveTarget._id}/approve`, buildPayload(updateExisting));
      toast.success(updateExisting || approveTarget.updateMemberId ? 'החבר עודכן' : 'הבקשה אושרה והחבר נוצר');
      setApproveTarget(null);
      setExistingMember(null);
      loadData();
    } catch (e) {
      const data = e.response?.data;
      if (e.response?.status === 409 && data?.code === 'PHONE_EXISTS') {
        setExistingMember(data.existingMember || null);
        setApproveError(data.error || 'החבר כבר רשום במערכת עם מספר טלפון זה');
      } else {
        setExistingMember(null);
        setApproveError(data?.error || 'שגיאה באישור');
      }
    } finally {
      setApproveLoading(false);
    }
  };

  const canEditId = (r) => r.status === 'approved' && !!r.memberId;
  const openMemberCard = (row) => setMemberCardId(String(row.memberId._id || row.memberId));
  const closeMemberCard = () => { setMemberCardId(null); loadData(); };

  const openReject = (row) => { setRejectTarget(row); setRejectNote(''); setRejectError(''); };
  const reject = async () => {
    setRejectLoading(true);
    setRejectError('');
    try {
      await api.post(`/pending-registrations/${rejectTarget._id}/reject`, { notes: rejectNote });
      toast.success('הבקשה נדחתה');
      setRejectTarget(null);
      loadData();
    } catch (e) {
      setRejectError(apiError(e, 'שגיאה בדחייה'));
    } finally {
      setRejectLoading(false);
    }
  };

  const counts = useMemo(() => ({
    pending: allData.filter((r) => r.status === 'pending').length,
    approved: allData.filter((r) => r.status === 'approved').length,
    rejected: allData.filter((r) => r.status === 'rejected').length,
    noId: allData.filter((r) => canEditId(r) && !r.memberId.idNumber).length,
    all: allData.length,
  }), [allData]);
  const rows = useMemo(() => {
    if (quick === 'all') return allData;
    if (quick === 'noId') return allData.filter((r) => canEditId(r) && !r.memberId.idNumber);
    return allData.filter((r) => r.status === quick);
  }, [allData, quick]);

  const columns = useMemo(() => [
    { key: 'createdAt', label: 'תאריך', type: 'date', render: (r) => (r.createdAt ? formatHebrewDateTime(r.createdAt) : '—') },
    { key: 'phone', label: 'טלפון מתקשר', ltr: true, copy: true },
    {
      key: 'phoneOverride',
      label: 'טלפון חלופי',
      ltr: true,
      copy: true,
      getValue: (r) => (r.noPhone ? 'ללא טלפון' : r.phoneOverride || ''),
      render: (r) => (r.noPhone ? <Tag tone="warning">ללא טלפון</Tag> : r.phoneOverride || '—'),
    },
    { key: 'status', label: 'סטטוס', type: 'enum', getValue: (r) => STATUS[r.status]?.label || r.status, render: (r) => { const s = STATUS[r.status] || { label: r.status, tone: 'neutral' }; return <Tag tone={s.tone}>{s.label}</Tag>; } },
    {
      key: 'memberIdNumber',
      label: 'ת״ז חבר',
      ltr: true,
      getValue: (r) => r.memberId?.idNumber || '',
      render: (r) => (r.memberId?.idNumber || (canEditId(r) ? <Tag tone="warning">חסר</Tag> : '—')),
    },
    { key: 'source', label: 'מקור', type: 'enum', getValue: (r) => (r.source === 'web' ? 'ווב' : 'ימות'), render: (r) => <Tag icon={r.source === 'web' ? 'link' : 'phone'}>{r.source === 'web' ? 'ווב' : 'ימות'}</Tag> },
    ...RECORDINGS.map(([rec, text, label]) => ({
      key: rec,
      label,
      sortable: false,
      filterable: false,
      getValue: (r) => (r.source === 'web' ? r[text] || '' : (r[rec] ? 'הקלטה' : '')),
      render: (r) => <RecOrText row={r} rec={rec} text={text} label={label} />,
    })),
    ...(IS_MISHTAMERET ? [
      { key: 'updateMemberId', label: 'סוג', type: 'enum', getValue: (r) => (r.updateMemberId ? 'עדכון פרטים' : 'חדש'), render: (r) => (r.updateMemberId ? <Tag tone="info">עדכון פרטים</Tag> : 'חדש') },
      { key: 'city', label: 'עיר', type: 'enum', render: (r) => r.city || <RecOrText row={r} rec="cityRecording" text="city" label="עיר" /> },
      { key: 'idNumber', label: 'ת״ז / קוד', ltr: true, copy: true },
      { key: 'occupation', label: 'סטטוס', type: 'enum' },
      { key: 'trackChoice', label: 'מסלול', type: 'enum' },
    ] : [
      { key: 'kolelName', label: IS_OPEN_GEMARA ? 'כולל' : 'כוללים', getValue: chosenKolels },
    ]),
    { key: 'examCode', label: 'קוד מבחן', ltr: true, getValue: (r) => r.examCode || r.examId?.code || '' },
    { key: 'examLocation', label: 'עיר מבחן', type: 'enum' },
    { key: 'examId', label: 'מבחן', getValue: (r) => (r.examId && typeof r.examId === 'object' ? `${r.examId.name || ''} ${r.examId.subject || ''}`.trim() : '') },
  ], []);

  const t = approveTarget;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const kolelSlot = (label, refKey, nameKey, options, extra = {}) => (
    <Field label={label}>
      <ComboBox
        value={kolels.find((k) => k._id === form[refKey]) || null}
        onChange={(v) => setForm((f) => ({ ...f, [refKey]: v?._id || null, [nameKey]: v?.name || '', ...extra }))}
        options={options}
        getLabel={kolelLabel}
        getKey={(o) => o?._id}
        placeholder="חיפוש לפי שם, עיר או קוד…"
        emptyText="לא נמצא כולל"
      />
    </Field>
  );

  return (
    <>
      <PageHeader
        title="בקשות רישום ממתינות"
        sub={!loading ? `${counts.pending.toLocaleString('he-IL')} ממתינות לטיפול · ${counts.all.toLocaleString('he-IL')} בסך הכול` : undefined}
        more={[{ key: 'refresh', label: 'רענון', icon: 'refresh', onClick: loadData }]}
      />

      <QuickFilters
        value={quick}
        onChange={(k) => setQuick(k)}
        loading={loading && !allData.length}
        items={[
          { key: 'pending', label: 'ממתינות', value: counts.pending, icon: 'clock', tone: 'warning', warn: counts.pending > 0 },
          { key: 'approved', label: 'אושרו', value: counts.approved, icon: 'check', tone: 'success' },
          { key: 'noId', label: 'אושרו ללא ת״ז', value: counts.noId, icon: 'alert', tone: 'warning' },
          { key: 'rejected', label: 'נדחו', value: counts.rejected, icon: 'x', tone: 'danger' },
          { key: 'all', label: 'הכול', value: counts.all, icon: 'list', tone: 'neutral' },
        ]}
      />

      <DataTable
        mode="client"
        rows={rows}
        rowKey="_id"
        columns={columns}
        loading={loading}
        error={error}
        onRetry={loadData}
        onRefresh={loadData}
        storageKey="bchinot-pending-registrations"
        defaultQuery={{ sort: { key: 'createdAt', dir: -1 } }}
        selectable={false}
        exportFilename="בקשות רישום"
        emptyTitle={quick === 'pending' ? 'אין בקשות ממתינות' : 'אין בקשות'}
        onRowClick={(r) => { if (r.status === 'pending') openApprove(r); else if (canEditId(r)) openMemberCard(r); }}
        rowActions={(r) => {
          if (r.status === 'pending') {
            return [
              { key: 'approve', icon: 'check', label: 'אשר ורשום חבר', onClick: () => openApprove(r) },
              { key: 'reject', icon: 'x', label: 'דחה', onClick: () => openReject(r) },
            ];
          }
          return canEditId(r) ? [{ key: 'card', icon: 'user', label: 'כרטיס חבר', onClick: () => openMemberCard(r) }] : [];
        }}
      />

      <Dialog
        open={!!t}
        onClose={() => !approveLoading && setApproveTarget(null)}
        title="אישור בקשת רישום"
        icon="check"
        size="md"
        footer={(
          <>
            <Button variant="ghost" disabled={approveLoading} onClick={() => setApproveTarget(null)}>ביטול</Button>
            {existingMember ? <Button icon="edit" disabled={approveLoading} onClick={() => approve(true)}>עדכן חבר קיים</Button> : null}
            <Button variant="primary" icon="check" disabled={approveLoading} onClick={() => approve(false)}>
              {approveLoading ? 'שומר…' : (t?.updateMemberId ? 'אשר ועדכן חבר' : 'אשר וצור חבר')}
            </Button>
          </>
        )}
      >
        {t ? (
          <div className="nx-stack">
            <dl className="nx-kv">
              <div className="nx-kv__row"><dt>טלפון מתקשר</dt><dd dir="ltr"><b>{t.phone}</b></dd></div>
              {t.phoneOverride ? <div className="nx-kv__row"><dt>טלפון חלופי</dt><dd dir="ltr"><b>{t.phoneOverride}</b></dd></div> : null}
              {t.noPhone ? <div className="nx-kv__row"><dt>טלפון לנרשם</dt><dd><Tag tone="warning">ללא טלפון</Tag></dd></div> : null}
              <div className="nx-kv__row"><dt>מקור</dt><dd>{t.source === 'web' ? 'ווב' : 'ימות'}</dd></div>
              {IS_MISHTAMERET && t.occupation ? <div className="nx-kv__row"><dt>סטטוס</dt><dd>{t.occupation}</dd></div> : null}
              {IS_MISHTAMERET && t.email ? <div className="nx-kv__row"><dt>מייל</dt><dd dir="ltr">{t.email}</dd></div> : null}
              {IS_MISHTAMERET && t.trackChoice ? <div className="nx-kv__row"><dt>מסלול</dt><dd>{t.trackChoice}</dd></div> : null}
            </dl>

            {t.updateMemberId ? (
              <Banner tone="warning" icon="alert">
                {`בקשת עדכון פרטים לחבר קיים: ${fullName(t.updateMemberId)}${t.updateMemberId.phone ? ` | טלפון נוכחי: ${t.updateMemberId.phone}` : ''}${t.updateMemberId.city ? ` | עיר נוכחית: ${t.updateMemberId.city}` : ''}. האישור יעדכן את החבר הקיים (שדות ריקים לא ידרסו).`}
              </Banner>
            ) : null}

            {t.noPhone ? (
              <Banner tone="warning" icon="alert">
                הנרשם הצהיר שאין לו טלפון — החבר ייפתח בלי טלפון (לא לפי מספר המתקשר) ויזדהה בקו לפי ת״ז. לא תישלח הודעה קולית.
              </Banner>
            ) : null}

            {t.source === 'web' ? (
              <Banner tone="info" icon="info">
                {`בקשה מהאתר — הפרטים מולאו מראש.${notifyEnabled ? ' ניתן לשלוח הודעה קולית לנרשם לפי הגדרות תבניות IVR.' : ' אוטומציית הודעת אישור כבויה (ניתן להפעיל במסך תבניות IVR).'}`}
              </Banner>
            ) : <h3 className="nx-sub-h">האזן להקלטות ומלא את הפרטים</h3>}
            {DIALOG_RECORDINGS.some(([rec]) => t[rec]) ? (
              <div className="nx-grid nx-grid--2">
                {DIALOG_RECORDINGS.filter(([rec]) => t[rec]).map(([rec, , label]) => <AudioPlayer key={rec} src={recSrc(t, rec)} label={label} />)}
              </div>
            ) : null}

            <FormGrid cols={2}>
              <Field label="שם פרטי *"><TextInput value={form.firstName} onChange={set('firstName')} data-autofocus /></Field>
              <Field label="שם משפחה *"><TextInput value={form.lastName} onChange={set('lastName')} /></Field>
              <Field label="שם האב"><TextInput value={form.fatherName} onChange={set('fatherName')} /></Field>
              <Field label="שם השווער"><TextInput value={form.fatherInLawName} onChange={set('fatherInLawName')} /></Field>
              <Field label={IS_MISHTAMERET ? 'ת״ז / קוד אישי' : 'ת״ז'}><TextInput value={form.idNumber} onChange={set('idNumber')} dir="ltr" inputMode="numeric" /></Field>
              {IS_MISHTAMERET ? (
                <Field label="סטטוס">
                  <Select
                    value={form.occupation}
                    onChange={(v) => setForm((f) => ({ ...f, occupation: v || '' }))}
                    placeholder="לא צוין"
                    options={[...OCCUPATION_OPTIONS, ...(form.occupation && !OCCUPATION_OPTIONS.includes(form.occupation) ? [form.occupation] : [])]}
                  />
                </Field>
              ) : null}
            </FormGrid>

            <h3 className="nx-sub-h">כתובת</h3>
            <FormGrid cols={2}>
              <Field label="עיר"><TextInput value={form.city} onChange={set('city')} /></Field>
              <Field label="רחוב"><TextInput value={form.street} onChange={set('street')} /></Field>
            </FormGrid>
            <FormGrid cols={3}>
              <Field label="מספר בניין"><TextInput value={form.buildingNumber} onChange={set('buildingNumber')} /></Field>
              <Field label="כניסה"><TextInput value={form.entrance} onChange={set('entrance')} /></Field>
              <Field label="דירה"><TextInput value={form.apartment} onChange={set('apartment')} /></Field>
            </FormGrid>

            {!IS_MISHTAMERET ? (
              <>
                <h3 className="nx-sub-h">{IS_OPEN_GEMARA ? 'כולל' : 'כוללים'}</h3>
                {IS_OPEN_GEMARA
                  ? kolelSlot('כולל', 'kolelMorningRef', 'kolelMorning', kolels, { kolelAfternoonRef: null, kolelAfternoon: '', kolelEveningRef: null, kolelEvening: '' })
                  : (
                    <FormGrid cols={3}>
                      {kolelSlot('כולל לפנה"צ', 'kolelMorningRef', 'kolelMorning', kolelsForSlot('morning', form.kolelMorningRef))}
                      {kolelSlot('כולל אחה"צ', 'kolelAfternoonRef', 'kolelAfternoon', kolelsForSlot('afternoon', form.kolelAfternoonRef))}
                      {kolelSlot('כולל ערב', 'kolelEveningRef', 'kolelEvening', kolelsForSlot('evening', form.kolelEveningRef))}
                    </FormGrid>
                  )}
                {chosenKolels(t) ? <small className="ck-muted">הנרשם בחר: <b>{chosenKolels(t)}</b></small> : null}
              </>
            ) : null}

            <Field label="הערות"><TextArea rows={2} value={form.notes} onChange={set('notes')} /></Field>
            <Checkbox
              checked={sendNotification}
              disabled={!notifyEnabled}
              onChange={setSendNotification}
              label={notifyEnabled ? 'שלח הודעה קולית לנרשם' : 'שלח הודעה קולית לנרשם (האוטומציה כבויה)'}
            />

            {approveError ? (
              <Banner tone="danger" icon="alert">
                {approveError}
                {existingMember ? (
                  <div style={{ marginTop: 6 }}>
                    {`חבר קיים: ${fullName(existingMember)}${existingMember.personalCode != null ? ` | קוד אישי: ${existingMember.personalCode}` : ''}${existingMember.phone ? ` | טלפון: ${existingMember.phone}` : ''}${existingMember.city ? ` | עיר: ${existingMember.city}` : ''}`}
                  </div>
                ) : null}
              </Banner>
            ) : null}
          </div>
        ) : null}
      </Dialog>

      <Sheet open={!!memberCardId} onClose={closeMemberCard} width={1080} ariaLabel="כרטיס חבר">
        {memberCardId ? <div className="nx-sheet-body"><MemberCardNext key={memberCardId} recordId={memberCardId} onClose={closeMemberCard} /></div> : null}
      </Sheet>

      <Dialog
        open={!!rejectTarget}
        onClose={() => !rejectLoading && setRejectTarget(null)}
        title="דחיית בקשת רישום"
        icon="x"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" disabled={rejectLoading} onClick={() => setRejectTarget(null)}>ביטול</Button>
            <Button variant="danger" icon="x" disabled={rejectLoading} onClick={reject}>{rejectLoading ? 'דוחה…' : 'דחה'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <Row><span>טלפון:</span><b dir="ltr">{rejectTarget?.phone}</b></Row>
          <Field label="סיבת דחייה (אופציונלי)"><TextArea rows={2} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} data-autofocus /></Field>
          {rejectError ? <Banner tone="danger" icon="alert">{rejectError}</Banner> : null}
        </div>
      </Dialog>
    </>
  );
}
